import { useEffect, useState } from "react";

interface GaugeChartProps {
  value: number;          // 0–100
  size?: number;          // px
  strokeWidth?: number;
  label?: string;
  sublabel?: string;
}

export default function GaugeChart({
  value,
  size = 160,
  strokeWidth = 14,
  label,
  sublabel,
}: GaugeChartProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setAnimatedValue(clamped), 50);
    return () => clearTimeout(t);
  }, [clamped]);

  const radius = (size - strokeWidth) / 2;
  const circumference = Math.PI * radius; // half circle
  const offset = circumference - (animatedValue / 100) * circumference;

  // Color thresholds
  const color =
    clamped >= 80 ? "hsl(142 71% 45%)" : clamped >= 50 ? "hsl(38 92% 50%)" : "hsl(0 84% 60%)";

  const cx = size / 2;
  const cy = size / 2;

  return (
    <div className="flex flex-col items-center" style={{ width: size }}>
      <svg width={size} height={size / 2 + strokeWidth} viewBox={`0 0 ${size} ${size / 2 + strokeWidth}`}>
        {/* Track */}
        <path
          d={`M ${strokeWidth / 2} ${cy} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${cy}`}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Value */}
        <path
          d={`M ${strokeWidth / 2} ${cy} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${cy}`}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1.2s ease-out" }}
        />
      </svg>
      <div className="-mt-6 text-center">
        <div className="text-3xl font-bold" style={{ color }}>
          {Math.round(animatedValue)}
        </div>
        {label && <div className="text-sm font-medium text-foreground">{label}</div>}
        {sublabel && <div className="text-xs text-muted-foreground">{sublabel}</div>}
      </div>
    </div>
  );
}
